import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input, Alert, FormGroup } from 'reactstrap';
import axios from 'axios';

interface Purchase {
    id?: number;
    purchase_id: number;
    product_title: string;
    product_type: string;
    quantity: number;
    description?: string;
    validity?: string | null;
    curr_prd_end?: string;
    curr_prd_strt?: string;
    pack_active: boolean;
    unit_price?: number;
    is_cancelled?: boolean;
    cancelled_quantity?: number;
    remaining_quantity?: number;
    cancellation_days?: number;
}

interface Order {
    id: number;
    order_id: string;
    order_uuid: string;
    total_price: number;
    payment_status: string;
    purchases: Purchase[];
    has_pending_cancellation?: boolean;
}

interface OrderCancellationModalProps {
    isOpen: boolean;
    toggle: () => void;
    order: Order | null;
    isAdmin?: boolean;
    onSuccess: () => void;
}

const CANCELLATION_REASONS = [
    { value: 'changed_mind', label: 'Changed my mind' },
    { value: 'found_alternative', label: 'Found a better alternative' },
    { value: 'too_expensive', label: 'Too expensive' },
    { value: 'not_expected', label: 'Not what I expected' },
    { value: 'technical_issues', label: 'Technical issues' },
    { value: 'poor_service', label: 'Poor customer service' },
    { value: 'no_longer_needed', label: 'No longer needed' },
    { value: 'accidental_purchase', label: 'Accidental purchase' },
    { value: 'duplicate_order', label: 'Duplicate order' },
    { value: 'service_not_described', label: 'Service not as described' },
    { value: 'other', label: 'Other (please specify)' },
];

const PRODUCT_TYPE_LABELS: Record<string, string> = {
    plan: 'Plan/Package',
    planoptionals: 'Optional',
    addon: 'Add-On',
    subscriptionaddon: 'Subscription',
    onetimeproduct: 'Product/Service',
};

const OrderCancellationModal: React.FC<OrderCancellationModalProps> = ({
    isOpen,
    toggle,
    order,
    isAdmin = false,
    onSuccess,
}) => {
    const [selectedPurchases, setSelectedPurchases] = useState<Set<number>>(new Set());
    const [quantities, setQuantities] = useState<Record<number, number>>({});
    const [cancellationReason, setCancellationReason] = useState<string>('changed_mind');
    const [customReason, setCustomReason] = useState<string>('');
    const [generalNotes, setGeneralNotes] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertMsg, setAlertMsg] = useState<{ open: boolean; type: string; content: string }>({
        open: false,
        type: 'success',
        content: '',
    });

    // Reset state when modal opens and select all products by default
    useEffect(() => {
        if (isOpen && order) {
            console.log('Order data received in modal:', order);

            // Select only purchases that haven't been fully cancelled
            const allPurchaseIds = new Set<number>();
            const initialQuantities: Record<number, number> = {};

            // For subscription products, get plan purchase to check cancellation_days
            const planPurchase = order.purchases.find(p => p.product_type === 'plan');
            
            order.purchases.forEach((purchase) => {
                const purchaseId = purchase.purchase_id || purchase.id!;
                const remainingQty = purchase.remaining_quantity ?? purchase.quantity;
                
                // Only include if there's remaining quantity to cancel
                if (remainingQty > 0) {
                    // Check cancellation period
                    let canCancel = true;
                    
                    if (purchase.product_type === 'onetimeproduct') {
                        // For one-time products, check individual cancellation_days
                        const check = isCancellationAllowed(purchase);
                        canCancel = check.allowed;
                    } else if (['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(purchase.product_type)) {
                        // For subscription products, use plan's cancellation_days
                        if (planPurchase) {
                            const check = isCancellationAllowed(planPurchase);
                            canCancel = check.allowed;
                        }
                    }
                    
                    if (canCancel) {
                        allPurchaseIds.add(purchaseId);
                        // For customers: set to all remaining quantity (they can't select)
                        // For admins: default to 1 (they can adjust)
                        initialQuantities[purchaseId] = isAdmin ? 1 : remainingQty;
                    }
                }
            });

            setSelectedPurchases(allPurchaseIds);
            setQuantities(initialQuantities);
            setCancellationReason('changed_mind');
            setCustomReason('');
            setGeneralNotes('');
            setAdminNotes('');
            setAlertMsg({ open: false, type: 'success', content: '' });
        }
    }, [isOpen, order]);

    const handlePurchaseToggle = (purchaseId: number) => {
        const newSelected = new Set(selectedPurchases);
        if (newSelected.has(purchaseId)) {
            newSelected.delete(purchaseId);
            // Clean up related states
            const newQuantities = { ...quantities };
            delete newQuantities[purchaseId];
            setQuantities(newQuantities);
        } else {
            // Validate that cancellation is still allowed before adding
            const purchase = order?.purchases.find(p => (p.purchase_id || p.id) === purchaseId);
            if (purchase) {
                const planPurchase = order?.purchases.find(p => p.product_type === 'plan');
                let canCancel = true;
                
                if (purchase.product_type === 'onetimeproduct') {
                    canCancel = isCancellationAllowed(purchase).allowed;
                } else if (['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(purchase.product_type)) {
                    if (planPurchase) {
                        canCancel = isCancellationAllowed(planPurchase).allowed;
                    }
                }
                
                if (!canCancel) {
                    setAlertMsg({ 
                        open: true, 
                        type: 'error', 
                        content: `Cancellation period has expired for ${purchase.product_title}. This product cannot be cancelled.` 
                    });
                    return;
                }
                
                newSelected.add(purchaseId);
                // Initialize with default values
                setQuantities({ ...quantities, [purchaseId]: 1 });
            }
        }
        setSelectedPurchases(newSelected);
    };

    const handleQuantityChange = (purchaseId: number, quantity: number) => {
        setQuantities({ ...quantities, [purchaseId]: quantity });
    };

    const getSelectedPurchases = (): Purchase[] => {
        if (!order) return [];
        return order.purchases.filter(p => selectedPurchases.has(p.purchase_id || p.id!));
    };

    const hasOnlyOnetimeProducts = (): boolean => {
        const selected = getSelectedPurchases();
        return selected.length > 0 && selected.every(p => p.product_type === 'onetimeproduct');
    };

    const hasSubscriptionProducts = (): boolean => {
        const selected = getSelectedPurchases();
        return selected.some(p => {
            const validity = p.validity;
            return validity && validity !== '' && validity !== 'lifetime' && validity !== 'custom' && ['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(p.product_type);
        });
    };

    // Check if cancellation is allowed for a purchase based on cancellation_days
    const isCancellationAllowed = (purchase: Purchase): { allowed: boolean; message?: string } => {
        if (!purchase.curr_prd_strt) {
            return { allowed: true }; // Default allow if purchase date missing
        }

        // Handle cancellation_days: 0 means no cancellation allowed, undefined/null defaults to 5
        const cancellationDays = purchase.cancellation_days !== undefined && purchase.cancellation_days !== null 
            ? purchase.cancellation_days 
            : 5;

        // If cancellation_days is 0, no cancellation is allowed
        if (cancellationDays === 0) {
            return {
                allowed: false,
                message: `Cancellation is not allowed for this product.`
            };
        }

        const purchaseDate = new Date(purchase.curr_prd_strt);
        const now = new Date();
        const daysSincePurchase = Math.floor((now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysSincePurchase > cancellationDays) {
            return {
                allowed: false,
                message: `Cancellation period expired. Cancellation is only allowed within ${cancellationDays} days of purchase.`
            };
        }

        return { allowed: true };
    };

    // Get plan purchase from order (for subscription products)
    const getPlanPurchase = (): Purchase | null => {
        if (!order) return null;
        return order.purchases.find(p => p.product_type === 'plan') || null;
    };

    const handleSubmit = async () => {
        // Validate order exists
        if (!order) {
            setAlertMsg({ open: true, type: 'error', content: 'Order information is missing. Please try again.' });
            return;
        }

        // Check if there are any products available to cancel
        const hasAvailableProducts = order.purchases.some(p => (p.remaining_quantity ?? p.quantity) > 0);
        if (!hasAvailableProducts) {
            setAlertMsg({ open: true, type: 'error', content: 'All products in this order have already been cancelled.' });
            return;
        }

        // Validate selection
        if (selectedPurchases.size === 0) {
            setAlertMsg({ open: true, type: 'error', content: 'Please select at least one product to cancel' });
            return;
        }

        // Validate that all selected products are still within cancellation period
        const planPurchase = order.purchases.find(p => p.product_type === 'plan');
        const invalidProducts: string[] = [];
        
        selectedPurchases.forEach(purchaseId => {
            const purchase = order.purchases.find(p => (p.purchase_id || p.id) === purchaseId);
            if (purchase) {
                let canCancel = true;
                if (purchase.product_type === 'onetimeproduct') {
                    canCancel = isCancellationAllowed(purchase).allowed;
                } else if (['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(purchase.product_type)) {
                    if (planPurchase) {
                        canCancel = isCancellationAllowed(planPurchase).allowed;
                    }
                }
                
                if (!canCancel) {
                    invalidProducts.push(purchase.product_title);
                }
            }
        });
        
        if (invalidProducts.length > 0) {
            setAlertMsg({ 
                open: true, 
                type: 'error', 
                content: `Cancellation period has expired for the following products: ${invalidProducts.join(', ')}. Please remove them from selection.` 
            });
            return;
        }

        // Validate reason
        if (!cancellationReason) {
            setAlertMsg({ open: true, type: 'error', content: 'Please select a reason for cancellation' });
            return;
        }
        if (cancellationReason === 'other' && !customReason?.trim()) {
            setAlertMsg({ open: true, type: 'error', content: 'Please provide a custom reason when selecting "Other"' });
            return;
        }

        try {
            setLoading(true);

            // Prepare items array - use the same reason for all items
            // For customers: cancel all remaining quantity (no quantity selection)
            // For admins: use selected quantity or default to 1
            const items = Array.from(selectedPurchases).map(purchaseId => {
                const purchase = order!.purchases.find(p => (p.purchase_id || p.id) === purchaseId);
                const remainingQty = purchase ? (purchase.remaining_quantity ?? purchase.quantity) : 1;
                
                // For customers: always cancel all remaining quantity
                // For admins: use selected quantity or default to remaining quantity
                const quantityToCancel = isAdmin 
                    ? (quantities[purchaseId] || remainingQty)
                    : remainingQty;
                
                return {
                    purchase_id: purchaseId,
                    quantity: quantityToCancel,
                    reason: cancellationReason,
                    custom_reason: customReason || '',
                };
            });

            const payload = {
                order_id: order!.order_id,
                items,
                notes: generalNotes,
                ...(isAdmin && { admin_notes: adminNotes }),
            };

            console.log('Submitting cancellation payload:', payload);

            const response = await axios.post(
                '/payments/customer/orders/cancel-request/',
                payload,
                { withCredentials: true }
            );

            if (response.status === 'SENT') {
                const itemCount = selectedPurchases.size;
                const productNames = getSelectedPurchases().map(p => p.product_title).slice(0, 3);
                const productList = productNames.join(', ');
                const moreText = itemCount > 3 ? ` and ${itemCount - 3} more` : '';
                
                // Different message for one-time products (refund request) vs subscription cancellation
                const isRefundRequest = hasOnlyOnetimeProducts();
                let successMessage = '';
                
                if (isRefundRequest) {
                    // For one-time products: refund request
                    if (isAdmin) {
                        successMessage = `Refund request created for ${itemCount} product(s) from Order #${order.order_uuid}: ${productList}${moreText}. You can now process the refund from the cancellations page.`;
                    } else {
                        successMessage = `Your refund request has been sent for ${itemCount} product(s) from Order #${order.order_uuid}: ${productList}${moreText}. Our team will review and process your refund shortly.`;
                    }
                } else {
                    // For subscription products: cancellation
                    successMessage = `Successfully cancelled ${itemCount} product(s) from Order #${order.order_uuid}: ${productList}${moreText}`;
                }
                
                setAlertMsg({
                    open: true,
                    type: 'success',
                    content: successMessage,
                });
                setTimeout(() => {
                    toggle();
                    onSuccess();
                }, 2500);
            }
        } catch (error: any) {
            console.error('Error submitting cancellation:', error);
            const errorMsg = error.response?.data?.error || 'Failed to submit cancellation request';
            setAlertMsg({ open: true, type: 'error', content: errorMsg });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    if (!order) return null;

    // Check if order has pending cancellation
    const hasPendingCancellation = order.has_pending_cancellation || false;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
            <ModalHeader toggle={toggle}>
                {isAdmin ? 'Process Order Cancellation' : 'Cancel Order / Request Refund'}
            </ModalHeader>
            <ModalBody>
                {hasPendingCancellation && (
                    <Alert color="warning" className="mb-3">
                        <i className="bx bx-info-circle me-2"></i>
                        <strong>Notice:</strong> This order already has a pending cancellation request. 
                        Please wait for it to be processed before submitting a new request.
                    </Alert>
                )}
                {alertMsg.open && (
                    <Alert
                        color={alertMsg.type === 'success' ? 'success' : 'danger'}
                        toggle={() => setAlertMsg({ ...alertMsg, open: false })}
                    >
                        {alertMsg.content}
                    </Alert>
                )}

                <div className="mb-3">
                    <h6 className="fw-bold">Order #{order.order_uuid}</h6>
                    <p className="text-muted small mb-0">Total: ${order.total_price?.toFixed(2)}</p>
                </div>

                <hr />

                <h6 className="fw-bold mb-3">Products to Cancel</h6>

                {(() => {
                    // Get plan purchase for subscription products
                    const planPurchase = order.purchases.find(p => p.product_type === 'plan');
                    
                    // Filter products that can be cancelled
                    const cancellableProducts = order.purchases.filter(p => {
                        const remainingQty = p.remaining_quantity ?? p.quantity;
                        if (remainingQty <= 0) return false;
                        
                        // Check cancellation period
                        if (p.product_type === 'onetimeproduct') {
                            return isCancellationAllowed(p).allowed;
                        } else if (['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(p.product_type)) {
                            if (planPurchase) {
                                return isCancellationAllowed(planPurchase).allowed;
                            }
                            return true; // Default allow if no plan found
                        }
                        return true;
                    });
                    
                    const nonCancellableProducts = order.purchases.filter(p => {
                        const remainingQty = p.remaining_quantity ?? p.quantity;
                        if (remainingQty <= 0) return false;
                        
                        // Check cancellation period
                        if (p.product_type === 'onetimeproduct') {
                            return !isCancellationAllowed(p).allowed;
                        } else if (['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(p.product_type)) {
                            if (planPurchase) {
                                return !isCancellationAllowed(planPurchase).allowed;
                            }
                            return false;
                        }
                        return false;
                    });
                    
                    if (cancellableProducts.length === 0 && nonCancellableProducts.length === 0) {
                        return (
                            <div className="alert alert-info">
                                <i className="bx bx-info-circle me-2"></i>
                                All products in this order have already been fully cancelled.
                            </div>
                        );
                    }
                    
                    return (
                        <>
                            {cancellableProducts.length === 0 && nonCancellableProducts.length > 0 && (
                                <div className="alert alert-warning mb-3">
                                    <i className="bx bx-info-circle me-2"></i>
                                    <strong>Notice:</strong> The cancellation period has expired for all products in this order. Cancellation is no longer available.
                                </div>
                            )}
                            {cancellableProducts.map((purchase) => {
                                const purchaseId = purchase.purchase_id || purchase.id!;
                                const isSelected = selectedPurchases.has(purchaseId);
                                const isOnetimeProduct = purchase.product_type === 'onetimeproduct';
                                const remainingQty = purchase.remaining_quantity ?? purchase.quantity;
                                return (
                                    <div
                                        key={purchaseId}
                                        className={`border rounded p-3 mb-3 ${isSelected ? 'border-primary bg-light' : ''}`}
                                    >
                            <div className="d-flex align-items-start">
                                {/* <FormGroup check className="me-3">
                                    <Input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled={true}
                                        onChange={() => handlePurchaseToggle(purchaseId)}
                                    />
                                </FormGroup> */}
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-0">
                                        <div>
                                            <h6 className="mb-1">
                                                {purchase.product_title}
                                                {isSelected && !isOnetimeProduct && purchase.curr_prd_end && (
                                                    <span className="ms-2" style={{ fontSize: '0.6rem', fontWeight: 'normal' }}>
                                                        (Access until {formatDate(purchase.curr_prd_end)})
                                                    </span>
                                                )}
                                            </h6>
                                            <span className="badge bg-info text-white fw-light">
                                                {PRODUCT_TYPE_LABELS[purchase.product_type] || purchase.product_type}
                                            </span>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-bold text-primary">
                                                ${(purchase.unit_price || 0).toFixed(2)}
                                            </div>
                                            {/* <small className="text-muted">
                                                Available: {remainingQty}
                                            </small> */}
                                            <small className="text-muted">
                                                Quantity: {purchase.quantity || 1}
                                            </small>
                                        </div>
                                    </div>

                                    {purchase.description && (
                                        <p className="text-muted small mb-2">{purchase.description}</p>
                                    )}

                                    {isSelected && (
                                        <>
                                            {/* Quantity selector - only for admins and when quantity > 1 */}
                                            {isAdmin && remainingQty > 1 && (
                                                <div className="mt-3 pt-3 border-top">
                                                    <FormGroup className="mb-3">
                                                        <Label className="fw-semibold small">
                                                            Quantity to Cancel (Available: {remainingQty})
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={remainingQty}
                                                            value={quantities[purchaseId] || 1}
                                                            onChange={(e) =>
                                                                handleQuantityChange(purchaseId, parseInt(e.target.value))
                                                            }
                                                            className="form-control-sm"
                                                        />
                                                    </FormGroup>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                                    );
                                })}
                            {nonCancellableProducts.length > 0 && (
                                <div className="mt-3">
                                    <h6 className="fw-bold text-muted mb-2">Products Not Available for Cancellation</h6>
                                    {nonCancellableProducts.map((purchase) => {
                                        const purchaseId = purchase.purchase_id || purchase.id!;
                                        const isOnetimeProduct = purchase.product_type === 'onetimeproduct';
                                        const planPurchase = order.purchases.find(p => p.product_type === 'plan');
                                        
                                        // Get cancellation check message
                                        let checkMessage = '';
                                        if (isOnetimeProduct) {
                                            const check = isCancellationAllowed(purchase);
                                            checkMessage = check.message || '';
                                        } else if (planPurchase) {
                                            const check = isCancellationAllowed(planPurchase);
                                            checkMessage = check.message || '';
                                        }
                                        
                                        return (
                                            <div key={purchaseId} className="border rounded p-3 mb-3 bg-light">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <h6 className="mb-1 text-muted">{purchase.product_title}</h6>
                                                        <span className="badge bg-secondary text-white fw-light">
                                                            {PRODUCT_TYPE_LABELS[purchase.product_type] || purchase.product_type}
                                                        </span>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold text-muted">
                                                            ${(purchase.unit_price || 0).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>
                                                {checkMessage && (
                                                    <div className="mt-2">
                                                        <small className="text-danger">
                                                            <i className="bx bx-info-circle me-1"></i>
                                                            {checkMessage}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    );
                })()}

                {/* Cancellation reason - single field for all products */}
                <FormGroup className="mt-4">
                    <Label className="fw-semibold">Reason for Cancellation <span className="text-danger">*</span></Label>
                    <Input
                        type="select"
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                    >
                        {CANCELLATION_REASONS.map((reason) => (
                            <option key={reason.value} value={reason.value}>
                                {reason.label}
                            </option>
                        ))}
                    </Input>
                </FormGroup>

                {/* Custom reason textarea */}
                {cancellationReason === 'other' && (
                    <FormGroup>
                        <Label className="fw-semibold">Please Specify <span className="text-danger">*</span></Label>
                        <Input
                            type="textarea"
                            rows={2}
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            placeholder="Please provide more details..."
                        />
                    </FormGroup>
                )}

                {/* General notes */}
                <FormGroup className="mt-3">
                    <Label className="fw-semibold">Additional Notes (Optional)</Label>
                    <Input
                        type="textarea"
                        rows={3}
                        value={generalNotes}
                        onChange={(e) => setGeneralNotes(e.target.value)}
                        placeholder="Any additional information you'd like to share..."
                    />
                </FormGroup>

                {/* Admin notes (admin only) */}
                {isAdmin && (
                    <FormGroup className="mt-3">
                        <Label className="fw-semibold">Admin Notes</Label>
                        <Input
                            type="textarea"
                            rows={2}
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            placeholder="Internal notes (not visible to customer)..."
                        />
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    color="danger" 
                    onClick={handleSubmit} 
                    disabled={loading || selectedPurchases.size === 0 || hasPendingCancellation}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="bx bx-x-circle me-2"></i>
                            {hasPendingCancellation ? 'Cancellation Already Requested' : 'Submit Cancellation'}
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default OrderCancellationModal;
